import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { TokenPair } from './types';

/**
 * Axios instance with the SPEC §5 auth contract:
 *  - access token lives ONLY in this module's memory (gone on reload);
 *  - refresh token lives in localStorage and survives reloads;
 *  - a 401 triggers ONE single-flight refresh (concurrent 401s share the same
 *    request), the failed call is retried once, and if the refresh itself
 *    fails the session is cleared and the app is told to show /login.
 */

const REFRESH_KEY = 'ribaAcc.refreshToken';
/** The PLATFORM_ADMIN's selected business — sent as X-Business-Id on every
 * call. Ignored by the backend for everyone else, so it is harmless to carry. */
const BUSINESS_KEY = 'ribaAcc.businessId';
const BUSINESS_HEADER = 'X-Business-Id';

let accessToken: string | null = null;
let businessId: string | null = readBusinessId();

function readBusinessId(): string | null {
  try {
    return localStorage.getItem(BUSINESS_KEY);
  } catch {
    return null;
  }
}

/** The selected business as the SERVER resolved it is `user.businessId` on
 * /auth/me — this module only carries the header. */
export function setBusinessId(id: string | null): void {
  businessId = id;
  if (id) localStorage.setItem(BUSINESS_KEY, id);
  else localStorage.removeItem(BUSINESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function hasRefreshToken(): boolean {
  return getRefreshToken() !== null;
}

export function setSession(pair: TokenPair): void {
  accessToken = pair.accessToken;
  localStorage.setItem(REFRESH_KEY, pair.refreshToken);
}

export function clearSession(): void {
  accessToken = null;
  localStorage.removeItem(REFRESH_KEY);
  setBusinessId(null);
}

/** Registered by AuthProvider: flips auth state to anonymous → route guard
 * redirects to /login. A plain callback keeps this module React-free. */
let onSessionExpired: () => void = () => {};
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

// `indexes: null` serializes array params as repeated keys
// (`storeIds=a&storeIds=b`) — the shape Express's default query parser turns
// back into an array; the bracket form `storeIds[]=a` would arrive as a
// literal key and be silently whitelisted away by the backend.
/** Hard ceiling on any single call. Without it a stalled mobile connection
 * (or a proxy waiting on a dead upstream) leaves spinners hanging forever —
 * the owner reported «login stuck loading». 20 s is generous for the slowest
 * report; anything longer is a transient failure the UI should surface. */
export const REQUEST_TIMEOUT_MS = 20_000;

export const api = axios.create({
  baseURL: '/api/v1',
  timeout: REQUEST_TIMEOUT_MS,
  paramsSerializer: { indexes: null },
});

/** Ceiling for calls that legitimately run long: MoySklad round-trips
 * (refresh crawl, return-draft sync, webhook registration, item moves that
 * mirror into MoySklad) and PDF rendering. Matches the web nginx read timeout. */
export const LONG_REQUEST_TIMEOUT_MS = 60_000;

/** True for failures that say nothing about the session: no network, timeout,
 * the api down/restarting behind the proxy (502/503/504), crashing (5xx),
 * throttled (429) or a request timeout (408). A refresh token must NOT be
 * thrown away on those — the user would be logged out by a flaky connection,
 * a deploy or a burst of retries. Only 401/403/4xx is a real verdict. */
export function isTransientError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === undefined) return true; // timeout / ERR_NETWORK / aborted
  return status >= 500 || status === 429 || status === 408;
}

/** A 5xx that is the api itself failing (not the proxy reporting it down):
 * worth showing as «server error», not «check your internet». */
export function isServerError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status ?? 0;
  return status >= 500 && status !== 502 && status !== 503 && status !== 504;
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  if (businessId) config.headers[BUSINESS_HEADER] = businessId;
  return config;
});

/** `rejected` = the server refused the token (revoked/expired) → session is
 * gone. `unreachable` = we never got a verdict → session kept, caller retries. */
export type RefreshResult = 'ok' | 'rejected' | 'unreachable';

let refreshPromise: Promise<RefreshResult> | null = null;
/** The error behind the last 'unreachable' verdict — surfaced to callers
 * instead of the data call's own 401, which would read as «Unauthorized». */
let lastRefreshError: unknown = null;

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return 'rejected';
  try {
    // Bare axios: must not run through the 401 interceptor recursively.
    const { data } = await axios.post<TokenPair>(
      '/api/v1/auth/refresh',
      { refreshToken },
      { timeout: REQUEST_TIMEOUT_MS },
    );
    setSession(data);
    return 'ok';
  } catch (error) {
    if (isTransientError(error)) {
      lastRefreshError = error;
      return 'unreachable';
    }
    clearSession();
    return 'rejected';
  }
}

/** Resolves once no token refresh is in flight. The service-worker update
 * reload waits on this: reloading mid-refresh would drop the response that
 * carries the new token pair. */
export function whenRefreshIdle(): Promise<void> {
  return refreshPromise ? refreshPromise.then(() => whenRefreshIdle()) : Promise.resolve();
}

/** Single-flight refresh: concurrent callers await the same request. */
export function refreshSession(): Promise<RefreshResult> {
  refreshPromise ??= doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as RetriableConfig | undefined;
  // /auth/* 401s are REAL answers (wrong password, bad old password) — a
  // refresh-and-retry there would turn "wrong old password" into a logout.
  if (
    error.response?.status === 401 &&
    config &&
    !config._retried &&
    !String(config.url).startsWith('/auth/')
  ) {
    config._retried = true;
    const result = await refreshSession();
    if (result === 'ok') return api(config);
    if (result === 'rejected') onSessionExpired();
    // 'unreachable': keep the session and report the transient failure
    // (timeout / api down) — not the stale 401 the data call got.
    else throw lastRefreshError ?? error;
  }
  throw error;
});

/** Human message out of an API error (Nest: {statusCode, message, error}). */
/** The machine-readable `code` a structured API error carries (the human
 * `message` is what apiErrorMessage returns). Used to branch on a specific
 * failure — e.g. DUPLICATE_SUSPECTED — instead of matching on prose. */
export function apiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string' ? data.code : null;
}

export function apiErrorMessage(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    const message = Array.isArray(data?.message) ? data.message.join('; ') : data?.message;
    return message ?? null;
  }
  return null;
}
